
import React, { useState, useEffect, useMemo } from "react";

import { toast } from "react-toastify";

import ApiCustom from "../../utils/ApiCustom";

import { formatDate } from "../../utils/helps";

import StatusDropdown from "../../components/admin/StatusDropdownButton";

import ConfirmPaidMembershipModal from "../ConfirmPaidMembershipModal";

import {
  BuildingOffice2Icon,
  PencilIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

const formatVND = (number) => {
  if (!number) return 0;

  const n =
    typeof number === "string"
      ? Number(number.toString().replace(/\D/g, ""))
      : number;

  return n.toLocaleString("vi-VN");
};

const getAmountNumber = (amount) => {
  if (!amount) return 0;

  return Number(amount.toString().replace(/[^\d]/g, ""));
};

const normalizeText = (value) => {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const getMemberType = (company) => {
  const memberType = normalizeText(company?.memberType);

  if (
    ["individual", "personal", "person", "ca nhan", "ca_nhan"].includes(
      memberType,
    )
  ) {
    return "individual";
  }

  return "company";
};

const getMemberTypeLabel = (company) => {
  return getMemberType(company) === "individual" ? "Cá nhân" : "Doanh nghiệp";
};

const getMemberTypeBadgeClass = (company) => {
  return getMemberType(company) === "individual" ? "badge-warning" : "badge-info";
};

const getMinimumFeeByMemberType = ({ company, membershipFee }) => {
  if (getMemberType(company) === "individual") {
    return Number(membershipFee?.individualFee || 0);
  }

  return Number(membershipFee?.fee || 0);
};

function HeaderSection({ membershipFee, totalPaid, onClose }) {
  return (
    <div className="p-6 rounded-lg sticky top-0 bg-base-100 z-20 pb-4">
      <div className="relative">
        <button
          onClick={onClose}
          className="absolute right-0 top-0 px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
        >
          X
        </button>

        <div className="flex-col md:flex md:flex-row md:justify-between md:items-start md:gap-6">
          <div className="md:hidden mb-2">
            <div className="text-2xl font-bold">{membershipFee?.title}</div>
          </div>

          <div className="flex flex-col">
            <h2 className="text-2xl font-bold mb-0.5 hidden md:block">
              Danh sách đóng hội phí
            </h2>

            <div className="flex flex-col gap-2 mt-1">
              <div className="flex items-center gap-2">
                <span className="badge badge-warning badge-outline gap-1">
                  <UserIcon className="w-4 h-4" />
                  Cá nhân
                </span>

                <span className="font-semibold text-yellow-700">
                  {formatVND(membershipFee?.individualFee || 0)}₫
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="badge badge-info badge-outline gap-1">
                  <BuildingOffice2Icon className="w-4 h-4" />
                  Doanh nghiệp
                </span>

                <span className="font-semibold text-blue-700">
                  {formatVND(membershipFee?.fee || 0)}₫
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start md:items-end mt-2 md:mt-0 md:mr-16">
            <div className="hidden md:block text-2xl font-bold mb-0.5">
              {membershipFee?.title}
            </div>

            <div className="text-green-600 font-bold text-lg text-left md:text-right whitespace-nowrap">
              Tổng đã đóng:&nbsp;
              <span className="text-2xl">{formatVND(totalPaid)}₫</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PayMembershipFeeModal = ({ membershipFeeId, onClose }) => {
  const [companies, setCompanies] = useState([]);

  const [membershipFee, setMembershipFee] = useState([]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const [pendingCompany, setPendingCompany] = useState(null);

  const [searchText, setSearchText] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");

  const [memberTypeFilter, setMemberTypeFilter] = useState("all");

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);

  const [noteCompany, setNoteCompany] = useState(null);

  const [noteValue, setNoteValue] = useState("");

  useEffect(() => {
    if (membershipFeeId) {
      getMembershipFee();
    }
  }, [membershipFeeId]);

  const getMembershipFee = () => {
    ApiCustom.get(`/membership-fee-shows/${membershipFeeId}`)
      .then((res) => {
        setMembershipFee(res.data?.data?.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (membershipFee && Array.isArray(membershipFee.companies)) {
      const updatedCompanies = membershipFee.companies.map((company) => ({
        ...company,
        isPaid: company?.CompaniesMustPayMembershipFee?.paid,
        amount: company?.CompaniesMustPayMembershipFee?.amount
          ? formatVND(company?.CompaniesMustPayMembershipFee?.amount) + "₫"
          : "",
        note: company?.CompaniesMustPayMembershipFee?.note || "",
      }));

      setCompanies(updatedCompanies);
    } else {
      setCompanies([]);
    }
  }, [membershipFee]);

  const handleAmountChange = (companyId, value) => {
    let rawValue = value.replace(/[^\d]/g, "");

    let formattedValue = rawValue ? formatVND(rawValue) + "₫" : "";

    const newCompanies = companies.map((company) =>
      company.id === companyId ? { ...company, amount: formattedValue } : company,
    );

    setCompanies(newCompanies);
  };

  const handleStatusChange = async (companyId, newStatus) => {
    const company = companies.find((c) => c.id === companyId);

    const updateCompanies = companies.map((company) =>
      company.id === companyId ? { ...company, isPaid: newStatus } : company,
    );

    const amountNumber = getAmountNumber(company.amount);

    const minFee = getMinimumFeeByMemberType({
      company,
      membershipFee,
    });

    if (!amountNumber || amountNumber < minFee) {
      toast.error(
        `Vui lòng nhập số tiền lớn hơn hoặc bằng ${formatVND(minFee)}₫!`,
      );

      return;
    }

    try {
      const updateData = {
        id: companyId,
        isPaid: newStatus,
        amount: amountNumber,
      };

      await ApiCustom.put(
        `/pay-membership-fee-update/${membershipFee.id}`,
        { paidCompany: updateData },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      ).then((res) => {
        if (res.status === 200) {
          setCompanies(updateCompanies);

          getMembershipFee();

          toast.success("Cập nhật trạng thái thanh toán thành công!");
        }
      });
    } catch {
      toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
    }
  };

  const getCompanyResentUserName = (company) => {
    const user = company?.users?.find(
      (user) => user?.CompaniesHasUsers?.isUserRepresentOfCompany,
    );

    return user ? user?.userFullName : "";
  };

  const handleExportExcel = async () => {
    try {
      const response = await ApiCustom(
        "/pay-membership-fee-export-excel/" + membershipFee?.id,
        { responseType: "blob" },
      );

      const blob = response.data;

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = url;

      a.download = "danh_sach_dong_hoi_phi.xlsx";

      document.body.appendChild(a);

      a.click();

      document.body.removeChild(a);
    } catch {}
  };


  const totalPaid =
    Array.isArray(companies) && companies.length > 0
      ? companies
          .filter((c) => c.isPaid)
          .reduce((sum, c) => sum + getAmountNumber(c.amount), 0)
      : 0;

  const visibleCompanies = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    let list = companies;

    if (q) {
      list = list.filter((c) => {
        const name = (c.companyName || "").toLowerCase();

        const rep = (getCompanyResentUserName(c) || "").toLowerCase();

        return name.includes(q) || rep.includes(q);
      });
    }

    if (statusFilter === "paid") {
      list = list.filter((c) => c.isPaid === true);
    } else if (statusFilter === "unpaid") {
      list = list.filter((c) => c.isPaid !== true);
    }

    if (memberTypeFilter !== "all") {
      list = list.filter((c) => getMemberType(c) === memberTypeFilter);
    }

    return list;
  }, [companies, searchText, statusFilter, memberTypeFilter]);

  const openNoteModal = (company) => {
    setNoteCompany(company);

    setNoteValue(company.note || "");

    setIsNoteModalOpen(true);
  };

  const handleUpdateNote = async () => {
    if (!noteCompany || !membershipFee?.id) return;

    try {
      const updateData = {
        id: noteCompany.id,
        note: noteValue?.trim() || "",
      };

      const res = await ApiCustom.put(
        `/pay-membership-fee-update/note/${membershipFee.id}`,
        { paidCompany: updateData },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.status === 200) {
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === noteCompany.id ? { ...c, note: updateData.note } : c,
          ),
        );

        toast.success("Cập nhật ghi chú thành công!");

        setIsNoteModalOpen(false);

        setNoteCompany(null);
      }
    } catch (error) {
      toast.error("Cập nhật ghi chú thất bại! Vui lòng thử lại.");
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl w-11/12 xl:w-2/3 max-h-[90vh] flex flex-col">
        <HeaderSection
          membershipFee={membershipFee}
          totalPaid={totalPaid}
          onClose={onClose}
        />

        {companies?.length > 0 ? (
          <div className="overflow-y-auto flex-grow overflow-x-hidden">
            <div className="sticky top-0 z-10 bg-base-100 border-b">
              <div className="px-6 py-3 pb-5 w-full flex flex-col lg:flex-row lg:items-center gap-3">
                <input
                  type="text"
                  className="input input-bordered w-full lg:max-w-sm"
                  placeholder="Tìm theo hội viên hoặc người đại diện…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />

                <select
                  className="select select-bordered w-full lg:w-auto"
                  value={memberTypeFilter}
                  onChange={(e) => setMemberTypeFilter(e.target.value)}
                >
                  <option value="all">Tất cả loại hội viên</option>

                  <option value="company">Doanh nghiệp</option>

                  <option value="individual">Cá nhân</option>
                </select>

                <select
                  className="select select-bordered w-full lg:w-auto"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Tất cả trạng thái</option>

                  <option value="paid">Đã đóng</option>

                  <option value="unpaid">Chưa đóng</option>
                </select>
              </div>
            </div>

            <div
              className="w-full overflow-x-auto px-6 pb-6"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <table className="table-auto w-full border-collapse border border-gray-300 my-1 min-w-[64rem]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Tên hội viên
                    </th>

                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Người đại diện
                    </th>

                    <th className="border border-gray-300 px-4 py-2 text-left w-60">
                      Số tiền đã đóng
                    </th>

                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Cập nhật
                    </th>

                    <th className="border border-gray-300 px-4 py-2 text-left w-64">
                      Ghi chú
                    </th>

                    <th className="border border-gray-300 px-4 py-2 text-center">
                      Trạng thái
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {visibleCompanies.length > 0 ? (
                    visibleCompanies.map((company) => (
                      <tr key={company.id} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-medium">
                              {company.companyName}
                            </span>

                            <span
                              className={`badge ${getMemberTypeBadgeClass(
                                company,
                              )} badge-outline gap-1`}
                            >
                              {getMemberType(company) === "individual" ? (
                                <UserIcon className="w-4 h-4" />
                              ) : (
                                <BuildingOffice2Icon className="w-4 h-4" />
                              )}

                              {getMemberTypeLabel(company)}
                            </span>
                          </div>
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          {getCompanyResentUserName(company)}
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9,\.]*"
                            className="input input-bordered w-full text-left"
                            value={company.amount || ""}
                            onChange={(e) =>
                              handleAmountChange(company.id, e.target.value)
                            }
                            placeholder={`Tối thiểu ${formatVND(
                              getMinimumFeeByMemberType({
                                company,
                                membershipFee,
                              }),
                            )}₫`}
                          />
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          {formatDate(
                            company?.CompaniesMustPayMembershipFee?.updatedAt,
                          )}
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className={`truncate max-w-[200px] ${
                                !company.note ? "italic text-gray-400" : ""
                              }`}
                              title={company.note || ""}
                            >
                              {company.note || "Không có ghi chú"}
                            </span>

                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              onClick={() => openNoteModal(company)}
                              aria-label="Chỉnh sửa ghi chú"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex justify-center">
                            <StatusDropdown
                              options={[
                                { value: true, label: "Đã đóng" },
                                { value: false, label: "Chưa đóng" },
                              ]}
                              value={company.isPaid}
                              onChange={(newStatus) => {
                                if (newStatus === true) {
                                  setPendingCompany(company);

                                  setIsConfirmModalOpen(true);
                                }
                              }}
                              getStatusStyle={(value) =>
                                value
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="border border-gray-300 px-4 py-6 text-center text-gray-500"
                      >
                        Không có kết quả phù hợp
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">Không có hội viên phải đóng</p>
        )}

        <div className="p-6 rounded-lg sticky bottom-0 bg-base-100 z-10">
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Đóng
            </button>

            {companies?.length > 0 && (
              <button
                type="button"
                className="px-4 py-2 border btn-outline btn-success rounded-md shadow-sm text-sm font-medium text-white "
                onClick={handleExportExcel}
              >
                Xuất Excel
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmPaidMembershipModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);

          setPendingCompany(null);
        }}
        onConfirm={() => {
          if (pendingCompany) {
            handleStatusChange(pendingCompany.id, true);

            setIsConfirmModalOpen(false);

            setPendingCompany(null);
          }
        }}
      />

      {isNoteModalOpen && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-40 flex items-center justify-center px-4">
          <div className="bg-base-100 rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Ghi chú cho: {noteCompany?.companyName}
              </h3>

              <button
                type="button"
                className="btn btn-xs btn-ghost"
                onClick={() => {
                  setIsNoteModalOpen(false);

                  setNoteCompany(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <textarea
                className="textarea textarea-bordered w-full min-h-[120px]"
                placeholder="Nhập ghi chú..."
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setIsNoteModalOpen(false);

                  setNoteCompany(null);
                }}
              >
                Hủy
              </button>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleUpdateNote}
              >
                Lưu ghi chú
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PayMembershipFeeModal;
